/**
 * Diario degli inneschi (F-168).
 *
 * La mappa qui sopra dice cosa e' **acceso**; questa card dice cosa ogni
 * innesco ha **deciso** negli ultimi giorni — comprese, e soprattutto, le
 * decisioni di non fare niente. E' la risposta alla domanda del 19/09/2026:
 * «ho il portafoglio crypto su Esegui davvero e nei tentativi non c'e' mai
 * niente di crypto: perche'?». Prima la risposta stava nel log del backend,
 * che in produzione nessuno legge.
 *
 * Le righe ripetute dal poller (ogni cinque minuti) arrivano gia' deduplicate
 * dal server: `occurrences` dice quante volte, `lastSeenAt` l'ultima.
 */

import { useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Card, DomainBadges, Spinner } from '@/ui';
import { useAutomationJournal, type JournalEntry, type JournalFlow, type JournalOutcome } from './api';

const FLOW_LABEL: Record<JournalFlow, string> = {
  market_open_trigger: 'Resoconto di mercato',
  portfolio_briefing: 'Digest di portafoglio',
  auto_trading_evaluate: 'Valutazione di auto-trading',
  auto_trading_resume: 'Ripresa proposte rinviate',
  profit_review: 'Scelta posizioni da vendere',
  market_close_trigger: 'Revisione di fine giornata',
  news_review: 'Lettura delle notizie',
};

const OUTCOME_LABEL: Record<JournalOutcome, string> = {
  done: 'fatto',
  skipped: 'niente da fare',
  deferred: 'rinviato',
  failed: 'fallito',
};

const OUTCOME_TONE: Record<JournalOutcome, 'positive' | 'neutral' | 'accent' | 'negative'> = {
  done: 'positive',
  skipped: 'neutral',
  deferred: 'accent',
  failed: 'negative',
};

type Filter = 'all' | 'crypto' | 'stock';

function matches(entry: JournalEntry, filter: Filter): boolean {
  if (filter === 'all') return true;
  // Una riga senza ambito riguarda entrambi: resta visibile in ogni filtro.
  return entry.domain === null || entry.domain === filter;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

export function AutomationJournal() {
  const [days, setDays] = useState(3);
  const [filter, setFilter] = useState<Filter>('all');
  const journal = useAutomationJournal(days);
  const entries = (journal.data?.entries ?? []).filter((entry) => matches(entry, filter));

  return (
    <Card
      title="Diario degli inneschi"
      subtitle="Cosa ha deciso ogni automazione, anche quando ha deciso di non fare niente. Una riga per decisione: se si ripete, conta le volte."
    >
      <div className="journal__filters" role="group" aria-label="Filtri del diario">
        {(
          [
            ['all', 'Tutto'],
            ['crypto', 'Solo crypto'],
            ['stock', 'Solo borsa'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`btn btn--sm ${filter === value ? 'btn--primary' : 'btn--ghost'}`}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
        <select
          className="field__input journal__days"
          aria-label="Giorni"
          value={days}
          onChange={(event) => setDays(Number(event.target.value))}
        >
          <option value={1}>Oggi</option>
          <option value={3}>Ultimi 3 giorni</option>
          <option value={7}>Ultimi 7 giorni</option>
          <option value={30}>Ultimi 30 giorni</option>
        </select>
      </div>

      {journal.isPending ? (
        <Spinner />
      ) : journal.error ? (
        <Alert variant="error" title="Diario non caricato">
          {errorMessage(journal.error)}
        </Alert>
      ) : entries.length === 0 ? (
        <p className="automations__muted">
          Nessuna decisione registrata nel periodo. Il diario si riempie dal prossimo giro degli inneschi
          automatici: se resta vuoto per un giorno intero con gli inneschi accesi, gli inneschi non stanno
          girando.
        </p>
      ) : (
        <ul className="journal">
          {entries.map((entry) => (
            <li key={entry.id} className={`journal__entry journal__entry--${entry.outcome}`}>
              <span className="journal__when">
                {formatDateTime(entry.lastSeenAt)}
                {entry.occurrences > 1 && <span className="automations__muted">×{entry.occurrences}</span>}
              </span>
              <div>
                <div className="journal__head">
                  <span className="journal__flow">{FLOW_LABEL[entry.flow] ?? entry.flow}</span>
                  <Badge tone={OUTCOME_TONE[entry.outcome]}>{OUTCOME_LABEL[entry.outcome] ?? entry.outcome}</Badge>
                  {entry.domain && <DomainBadges domains={[entry.domain]} />}
                  <code className="automations__code">{entry.reasonCode}</code>
                </div>
                <p className="journal__detail">{entry.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
